/**
 * Modal pour reporter une séance vers une autre date — utilisé pour
 * programmer un cours de rattrapage.
 *
 * Si la séance fait partie d'un tronc commun, l'API reporte automatiquement
 * toutes les séances du groupe ensemble (cohérence).
 */
import { useState } from 'react';
import { X, CalendarClock, AlertTriangle, Loader2 } from 'lucide-react';
import { useReporterSeanceMutation, type SeanceItem } from '../../services/vieScolaireApi';

interface Props {
  seance: SeanceItem;
  onClose: () => void;
  onReported: () => void;
}

const CRENEAUX_RAPIDE = [
  { label: '08:00 – 10:00', hd: '08:00', hf: '10:00' },
  { label: '10:15 – 12:15', hd: '10:15', hf: '12:15' },
  { label: '13:00 – 15:00', hd: '13:00', hf: '15:00' },
  { label: '15:15 – 17:15', hd: '15:15', hf: '17:15' },
  { label: '17:15 – 19:15', hd: '17:15', hf: '19:15' },
];

function isoJour(d: Date): string { return d.toISOString().slice(0, 10); }

export default function ModalReporterSeance({ seance, onClose, onReported }: Props) {
  // Par défaut : samedi suivant (cas typique de rattrapage)
  const proposerSamedi = () => {
    const d = new Date(seance.date);
    while (d.getDay() !== 6) d.setDate(d.getDate() + 1);
    return isoJour(d);
  };

  const [nouvelleDate, setNouvelleDate] = useState(proposerSamedi);
  const [heureDebut, setHeureDebut] = useState(seance.heure_debut);
  const [heureFin, setHeureFin] = useState(seance.heure_fin);
  const [salle, setSalle] = useState(seance.salle || '');
  const [motif, setMotif] = useState('');
  const [error, setError] = useState('');
  const [reporter, { isLoading }] = useReporterSeanceMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!nouvelleDate || nouvelleDate === seance.date) {
      setError('La nouvelle date doit être différente de la date actuelle.');
      return;
    }
    try {
      const res = await reporter({
        seanceId: seance.id,
        date_nouvelle: nouvelleDate,
        heure_debut: heureDebut,
        heure_fin: heureFin,
        salle, motif,
      }).unwrap();
      const msg = res.nb_reportes > 1
        ? `${res.nb_reportes} séances (tronc commun) reportées au ${new Date(nouvelleDate).toLocaleDateString('fr-FR')}.`
        : `Séance reportée au ${new Date(nouvelleDate).toLocaleDateString('fr-FR')}.`;
      alert(msg);
      onReported();
    } catch (err: any) {
      setError(err?.data?.detail || err?.message || 'Erreur lors du report.');
    }
  };

  const choisirCreneau = (idx: number) => {
    setHeureDebut(CRENEAUX_RAPIDE[idx].hd);
    setHeureFin(CRENEAUX_RAPIDE[idx].hf);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-start justify-between gap-2">
          <div>
            <h3 className="font-bold text-base text-gray-900 dark:text-white inline-flex items-center gap-2">
              <CalendarClock size={16} className="text-amber-500" /> Reporter (rattrapage)
            </h3>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {seance.matiere} • {seance.classe} • {seance.heure_debut}–{seance.heure_fin}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Date actuelle : {new Date(seance.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {seance.tronc_commun_groupe && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-2.5 text-xs text-purple-700 dark:text-purple-300">
              ℹ️ Cette séance fait partie d'un <strong>tronc commun</strong>. Toutes les classes concernées seront reportées ensemble.
            </div>
          )}

          {/* Nouvelle date */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Nouvelle date <span className="text-red-500">*</span>
            </label>
            <input type="date" value={nouvelleDate} onChange={(e) => setNouvelleDate(e.target.value)}
              min={isoJour(new Date())} required
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100" />
          </div>

          {/* Créneaux rapides + horaires libres */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Horaire
            </label>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {CRENEAUX_RAPIDE.map((c, idx) => {
                const actif = heureDebut === c.hd && heureFin === c.hf;
                return (
                  <button key={idx} type="button" onClick={() => choisirCreneau(idx)}
                    className={`px-2 py-1.5 text-xs rounded-lg border ${
                      actif
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                    }`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 items-center text-xs">
              <input type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)}
                className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
              <span className="text-gray-500">à</span>
              <input type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)}
                className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
          </div>

          {/* Salle */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Salle <span className="font-normal text-gray-500 normal-case">(optionnel)</span>
            </label>
            <input value={salle} onChange={(e) => setSalle(e.target.value)}
              maxLength={50} placeholder={`Salle actuelle : ${seance.salle || '—'}`}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100" />
          </div>

          {/* Motif */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Motif du report <span className="font-normal text-gray-500 normal-case">(optionnel)</span>
            </label>
            <textarea value={motif} onChange={(e) => setMotif(e.target.value)}
              rows={2} maxLength={300}
              placeholder="Ex. : absence formateur, jour férié, séance reprise…"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 resize-none" />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg p-2 text-sm flex items-start gap-2">
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-2.5 text-xs text-amber-700 dark:text-amber-300">
            La séance actuelle sera marquée comme « reportée » (conservée pour audit) et une nouvelle séance de rattrapage sera créée à la date choisie.
          </div>
        </div>

        <footer className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading}
            className="px-4 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />}
            {isLoading ? 'Report…' : 'Reporter'}
          </button>
        </footer>
      </form>
    </div>
  );
}
